import { trim } from 'lodash';
import { DateTime } from 'luxon';

import type {
  ActionInput,
  DetailedCategorizationActionLineInput,
  Maybe,
  Metadata,
  MultiWalletTransferRuleInput,
  RuleComparisonInput,
  TransferRuleInput,
} from '@/api-svc-types';
import { MULTI_WALLET_TRANSFER_RULE_TYPE } from '@/api-svc-types';

// Internal form shape uses `walletIds: string[]` regardless of which backend
// rule variant we'll eventually send. Saves are dispatched to the right
// envelope (`transfer:` vs `multiWalletTransfer:`) by ruleVarFactory.
export type RuleFormData = Omit<
  MultiWalletTransferRuleInput,
  'valueRules' | 'action' | 'afterDateSEC' | 'beforeDateSEC'
>;
export type TransferRuleData = RuleFormData;

type Dates = {
  afterDate: string;
  beforeDate: string;
  timezone: string;
};

function strTypeGuard(str: Maybe<string> | undefined | null): asserts str is string {
  if (!str) {
    throw Error('Cannot be empty string, or undefined, or null');
  }
}

const mapActionDataToActionInput = (action: ActionInput): ActionInput => {
  const {
    type,
    lines,
    categoryId,
    contactId,
    feeContactId,
    feeCategoryId,
    feePercentageSplits,
    percentageSplits,
    deFiWalletId,
    ignoreFailPricing,
    disposedCategoryId,
    disposedContactId,
    acquiredCategoryId,
    acquiredContactId,
    isIntercompanyTransfer,
    labelIds,
  } = action;

  //  BW4-2662: labels are an optional attribute on every categorization
  //  action. Ignore drops them by design (the BE adapter does the same).
  //  Coerce empty array → undefined so the mutation input stays clean.
  const labels = labelIds && labelIds.length > 0 ? labelIds : undefined;

  if (type === 'Ignore') {
    return {
      type,
    };
  }

  if (type === 'SimpleCategorization') {
    strTypeGuard(categoryId);
    strTypeGuard(contactId);
    return {
      type,
      categoryId,
      contactId,
      feeCategoryId,
      feeContactId,
      ignoreFailPricing,
      labelIds: labels,
    };
  }

  if (type === 'TradeCategorization') {
    strTypeGuard(feeContactId);
    return {
      type,
      feeContactId,
      ignoreFailPricing,
      labelIds: labels,
    };
  }

  if (type === 'InternalTransferCategorization') {
    strTypeGuard(feeContactId);
    return {
      type,
      feeContactId,
      feeCategoryId,
      ignoreFailPricing,
      labelIds: labels,
    };
  }

  if (type === 'FeeOnlyCategorization') {
    strTypeGuard(feeContactId);
    return {
      type,
      feeContactId,
      feeCategoryId,
      ignoreFailPricing,
      labelIds: labels,
    };
  }

  if (type === 'IntercompanyTransferCategorization') {
    strTypeGuard(feeContactId);
    return {
      type,
      feeContactId,
      feeCategoryId,
      disposedCategoryId,
      disposedContactId,
      acquiredCategoryId,
      acquiredContactId,
      ignoreFailPricing,
      labelIds: labels,
    };
  }

  if (type === 'SimpleSplitCategorization') {
    feePercentageSplits?.map((split) => {
      split.percentage = parseInt(split.percentage.toString());
    });
    percentageSplits?.map((split) => {
      split.percentage = parseInt(split.percentage.toString());
    });
    return {
      type,
      feePercentageSplits,
      percentageSplits,
      labelIds: labels,
    };
  }
  if (type === 'DetailedCategorization') {
    const lines = action.lines ?? [];
    return {
      type,
      ignoreFailPricing,
      isIntercompanyTransfer,
      lines: lines.length
        ? lines.map((line: DetailedCategorizationActionLineInput) => {
            const {
              lineQualifierExtractor,
              metadataIds,
              labelIds: lineLabelIds,
              ...otherAttrs
            } = line as DetailedCategorizationActionLineInput & { labelIds?: string[] | null };
            const { __typename, ...others } = otherAttrs as any;
            return {
              ...others,
              // eslint-disable-next-line no-unneeded-ternary
              lineQualifierExtractor: lineQualifierExtractor ? lineQualifierExtractor : null,
              metadataIds: metadataIds && metadataIds.length ? metadataIds.filter((id: string | null) => id) : null,
              labelIds: lineLabelIds && lineLabelIds.length > 0 ? lineLabelIds : undefined,
            };
          })
        : null,
      labelIds: labels,
    };
  }

  if (type === 'DeFiCategorization') {
    strTypeGuard(feeContactId);
    strTypeGuard(deFiWalletId);
    return {
      type,
      feeContactId,
      deFiWalletId,
      labelIds: labels,
    };
  }

  throw Error('Invalid parameter, please check Contact Id, Category Id, and Categorization.');
};

const validateValueRules = (valueRule: RuleComparisonInput) => {
  const { comparison, value } = valueRule;
  strTypeGuard(comparison);
  strTypeGuard(value);
};

export const mapTransferRulDataToTransferRuleInput = (
  rule: RuleFormData,
  valueRules: RuleComparisonInput[],
  action: ActionInput,
  dates: Dates
): MultiWalletTransferRuleInput => {
  const { timezone, afterDate, beforeDate } = dates;

  //  luxon lib toSeconds return decimal
  const afterDateSECWithMill = afterDate
    ? DateTime.fromISO(afterDate, { zone: timezone }).startOf('day').toSeconds()
    : undefined;

  const afterDateSEC = afterDateSECWithMill ? Math.round(afterDateSECWithMill) : undefined;

  const beforeDateSECWithMill = beforeDate
    ? DateTime.fromISO(beforeDate, { zone: timezone }).endOf('day').toSeconds()
    : undefined;

  const beforeDateSEC = beforeDateSECWithMill ? Math.floor(beforeDateSECWithMill) : undefined;

  const actionInput = mapActionDataToActionInput(action);
  const type = actionInput.type;

  valueRules.forEach(validateValueRules);

  const transferRule: Partial<MultiWalletTransferRuleInput> = {
    action: actionInput,
    valueRules: valueRules.length ? valueRules : undefined,
    afterDateSEC,
    beforeDateSEC,
  };

  for (const entry of Object.entries(rule)) {
    const [key, value] = entry as [keyof RuleFormData, any];

    if (key === 'fromAddress' && value) {
      const trimmedFromAddress = trim(value);
      transferRule[key] = trimmedFromAddress;
      continue;
    }

    if (key === 'toAddress' && value) {
      const trimmedToAddress = trim(value);
      transferRule[key] = trimmedToAddress;
      continue;
    }

    if (type === 'DetailedCategorization') {
      if (key === 'autoReconcile' || key === 'collapseValues') {
        transferRule[key] = undefined;
        continue;
      }
    }

    if (value !== '' && value !== null && value !== undefined) {
      transferRule[key] = value as any;
    } else {
      transferRule[key] = undefined;
    }
  }

  return transferRule as MultiWalletTransferRuleInput;
};

// Build the `Rule` GraphQL input envelope. Exactly one of `transfer` or
// `multiWalletTransfer` must be set — the backend rejects requests that
// include both.
//
// Dispatch:
//   - useMultiWallet=false (org without the MULTI_WALLET_RULES flag): send
//     the legacy `transfer:` envelope. The form's internal walletIds[] is
//     collapsed back to a single nullable walletId. If the user somehow
//     selected ≥2 wallets we only persist the first one — but the form
//     should not have offered a multi-select in that case.
//   - useMultiWallet=true: send the new `multiWalletTransfer:` envelope.
//     This is the recommended shape once the flag is on, even for a
//     single-wallet selection, so the org's rules have a consistent shape.
export const ruleVarFactory = (
  rule: MultiWalletTransferRuleInput,
  options?: { useMultiWallet?: boolean }
): { transfer?: TransferRuleInput; multiWalletTransfer?: MultiWalletTransferRuleInput } => {
  if (options?.useMultiWallet) {
    return { multiWalletTransfer: rule };
  }
  const { walletIds, ...rest } = rule;
  const walletId = walletIds && walletIds.length ? walletIds[0] : undefined;
  const legacy: TransferRuleInput = { ...rest, walletId };
  return { transfer: legacy };
};

// True when the persisted rule should be saved/updated via the
// `multiWalletTransfer:` envelope. Existing multi-wallet rules must stay
// multi-wallet (we don't auto-convert), and on flagged orgs new rules
// default to the multi-wallet shape.
export function shouldUseMultiWalletEnvelope(isFlagOn: boolean, existingRuleType?: string | null): boolean {
  if (existingRuleType === MULTI_WALLET_TRANSFER_RULE_TYPE) return true;
  return isFlagOn && !existingRuleType;
}

// Pull the wallet selection out of any rule variant the UI may have
// received. The legacy TransferRule has walletId: string|null; the new
// MultiWalletTransferRule has walletIds: string[]. Either is collapsed to
// the form's internal walletIds: string[] shape.
export function extractWalletIdsFromRule(
  rule: { walletId?: string | null; walletIds?: ReadonlyArray<string | null> | null } | null | undefined
): string[] {
  if (!rule) return [];
  if (Array.isArray(rule.walletIds)) return normalizeWalletIds(rule.walletIds);
  if (rule.walletId) return [rule.walletId];
  return [];
}

// Coerce any historical/predefined form value into the [String!]! shape the
// backend now expects. Accepts: array (filtered/typed), single string (wrapped),
// null/undefined (empty array). Empty array semantically means "all wallets".
export function normalizeWalletIds(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((w): w is string => typeof w === 'string' && !!w);
  }
  if (typeof value === 'string' && value) {
    return [value];
  }
  return [];
}

export function summarizeWalletIds(
  walletIds: ReadonlyArray<string | null | undefined> | null | undefined,
  resolveName: (id: string) => string
): string {
  const ids = normalizeWalletIds(walletIds);
  if (ids.length === 0) return 'All wallets';
  if (ids.length === 1) return resolveName(ids[0]) || ids[0];
  const first = resolveName(ids[0]) || ids[0];
  return `${first} +${ids.length - 1} more`;
}

export function snakeToPascal(str: string): string {
  return str.replace(/^[a-z]/, (c) => c.toUpperCase()).replace(/_[a-z]/g, (g) => g[1].toUpperCase());
}

export function pascalToSnake(str: string): string {
  return str.replace(/[a-z][A-Z]/g, (g) => g[0] + '_' + g[1]).toLowerCase();
}

export type MetadataByType = {
  type: string;
  metadata: Metadata[];
};

export function groupMetadataByType(metadata: Metadata[]): MetadataByType[] {
  const map = new Map<string, Metadata[]>();

  for (const m of metadata) {
    const exist = map.get(m.remoteType);
    if (!exist) {
      map.set(m.remoteType, [m]);
    } else {
      exist.push(m);
    }
  }

  return Array.from(map.entries()).map(([type, metadata]) => {
    return {
      type,
      metadata,
    };
  });
}

// Every category id a rule's action references, across all action shapes.
// Keys are the aliased names the RulesGrid queries return.
export function collectRuleCategoryIds(action: unknown): string[] {
  const a = action as Record<string, unknown> | null | undefined;
  if (!a) return [];
  const ids = new Set<string>();
  const directKeys = [
    'categoryId',
    'feeCategoryId',
    'internalFeeCategoryId',
    'feeOnlyFeeCategoryId',
    'intercompanyFeeCategoryId',
    'disposedCategoryId',
    'acquiredCategoryId',
  ];
  for (const key of directKeys) {
    const v = a[key];
    if (typeof v === 'string' && v) ids.add(v);
  }
  for (const arr of [a.splits, a.feeSplits, a.lines]) {
    if (!Array.isArray(arr)) continue;
    for (const item of arr) {
      const v = (item as { categoryId?: unknown })?.categoryId;
      if (typeof v === 'string' && v) ids.add(v);
    }
  }
  return [...ids];
}
